import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import 'reflect-metadata';
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps, GetStaticPropsContext } from 'next';
import { wrapper } from 'src/store';
import { i18n } from 'next-i18next.config';
import { listingService } from 'src/services/listing';
import { ListingBanner, ListingFilterKeys, ListingRelation } from 'src/enums/listing';
import styles from './listing.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AuthSelectors } from 'src/store/auth';
import { ListingViewPageActions } from 'src/store/pages/listing-view/actions';
import { ListingViewPageSelectors } from 'src/store/pages/listing-view/selectors';
import ListingsBanner from 'src/components/pages/listings/banner/banner';
import ListingsTableDocuments from 'src/components/pages/listings/table-documents/table-documents';
import Spinner, { SpinnerSize } from 'src/components/ui-kit/spinner/spinner';
import ListingDetails from 'src/components/pages/listings/details/details';
import ListingsMaps from 'src/components/pages/listings/maps/maps';
import { LayoutType } from '../../components/layout/layout';
import { AppState } from '../../store/state';
import { Store } from 'redux';
import ListingsBannerCreateAccount from '../../components/pages/listings/banner/create-account/create-account';
import { useEffect } from 'react';
import useListingIdFromRoute from 'src/hooks/use-listing-id-from-route';
import { configuration } from '../../configurations/configuration';
import { loadListingViewTask } from '../../store/pages/listing-view';
import Head from 'next/head';
import { ProfileSelectors } from 'src/store/profile';

const ListingsViewPage = (): JSX.Element => {
  const listing = useSelector(ListingViewPageSelectors.listing);
  const error = useSelector(ListingViewPageSelectors.error);
  const isAuthenticated = useSelector(AuthSelectors.isAuthenticated);
  const isLoading = useSelector(ListingViewPageSelectors.isLoading);
  const profile = useSelector(ProfileSelectors.profile);

  const listingId = useListingIdFromRoute();

  const dispatch = useDispatch();

  const title = `Buy mineral rights in ${listing?.county.nameWithoutPrefix} county ${listing?.state.name} - ${
    listing?.netAcres ? `${listing?.roundedNetAcres} NMA` : `$${listing?.roundedAverageMonthlyCashFlow}/mo`
  }`;

  const listingRelations = [
    ListingRelation.COUNTY,
    ListingRelation.STATE,
    ListingRelation.MEDIA_MAPS,
    ListingRelation.MEDIA_DOCUMENTS
  ];

  useEffect(() => {
    if (isAuthenticated && profile?.isActive) {
      dispatch(
        ListingViewPageActions.loadListingView({
          id: listingId,
          relations: listingRelations
        })
      );
    }
  }, [isAuthenticated, profile?.id]);

  useEffect(() => {
    if (error || listing === null) {
      dispatch(ListingViewPageActions.resetState());
      dispatch(
        ListingViewPageActions.loadListingView({
          id: listingId,
          relations: isAuthenticated && profile?.isActive ? listingRelations : undefined
        })
      );
    }
  }, [error, listing]);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta
          name='description'
          content='Do you buy mineral rights or royalties? We have properties available through our listing platform which is completely free for buyers.'
        />
        {!listing?.isIndex && <meta
          name='robots'
          content='noindex' />}
      </Head>
      {!isLoading && (
        <>
          {!isAuthenticated && <ListingsBannerCreateAccount listing={listing} />}
          {listing && listing?.banner !== ListingBanner.HIDDEN && listing?.banner !== ListingBanner.OFFMARKET && (
            <ListingsBanner type={listing?.bannerInUpperCase} />
          )}
          {profile?.isInactive && <ListingsBanner type={profile.statusInUpperCase} />}
          {isAuthenticated && profile?.isActive && listing?.mediaMaps && listing.mediaMaps.length > 0 && (
            <ListingsMaps
              maps={listing?.mediaMaps}
              className={styles.listing_box} />
          )}
          {isAuthenticated && profile?.isActive && listing?.mediaDocuments && listing.mediaDocuments.length > 0 && (
            <ListingsTableDocuments
              documents={listing.mediaDocuments}
              listingId={listing.id}
              linkArchive={listing.archive}
              isDownloadable
              className={styles.listing_box}
            />
          )}
          {listing && <ListingDetails
            listing={listing}
            className={styles.listing_box} />}
        </>
      )}
      {isLoading && (
        <div className={styles.listing_spinner}>
          <Spinner size={SpinnerSize.LARGE} />
        </div>
      )}
    </>
  );
};

ListingsViewPage.layout = LayoutType.ACCOUNT;

export default ListingsViewPage;

export const getStaticPaths: GetStaticPaths = async () => {
  const result: GetStaticPathsResult = {
    paths: [],
    fallback: 'blocking'
  };

  if (!('GENERATE_STATIC_PATHS' in process.env)) {
    return result;
  }

  const { data: listing } = await listingService.search({
    all: true,
    filters: {
      listings: ListingFilterKeys.ALL
    }
  });

  result.paths = listing.map((item) => ({ params: { id: item.id.toString() } }));

  return result;
};

export const getStaticProps: GetStaticProps = wrapper.getStaticProps(
  (store: Store<AppState>) => async ({ locale, params, preview }: GetStaticPropsContext) => {
    if (preview) {
      return {
        redirect: {
          permanent: false,
          destination: configuration.redirectPaths.checkPreview
        }
      };
    }

    const id = Number(params?.id);

    store.dispatch(ListingViewPageActions.resetState());

    store.dispatch(ListingViewPageActions.loadListingViewForServer({ id }));

    await loadListingViewTask?.toPromise();

    const error = store.getState().pages.listingView.error;

    if (error) {
      return {
        notFound: true
      };
    }

    return {
      props: {
        ...(await serverSideTranslations(locale ?? i18n.defaultLocale, [
          'common',
          'components:layout',
          'components:pages:restore-password',
          'components:pages:listings:view:maps',
          'components:pages:listings:view:details',
          'components:pages:listings:view:banner',
          'components:pages:listings:view:documents',
          'components:pages:listings:view:banner-create-account',
          'components:pages:listings:view'
        ]))
      },
      revalidate: configuration.revalidation.timeSec
    };
  }
);
